<template>
  <ion-footer class="site-manager-footer">
    <nav class="footer-menu" aria-label="Navigation principale">
      <button v-if="isValidated"
              type="button"
              class="footer-menu-item"
              v-bind:class="{ 'is-selected': isUnderPath('/site-manager/quotation-requests') }"
              v-bind:aria-current="isUnderPath('/site-manager/quotation-requests') ? 'page' : undefined"
              v-bind:aria-label="t('tabs.quotations')"
              v-on:click="goTo('SiteManagerQuotationRequests')">
        <ion-icon v-bind:icon="pricetagOutline" class="footer-menu-icon"/>
        <span class="footer-menu-label">{{ t('tabs.quotations') }}</span>
      </button>
      <button v-else class="footer-menu-item footer-placeholder" disabled aria-hidden="true" tabindex="-1"></button>

      <button v-if="isValidated"
              type="button"
              class="footer-menu-item"
              v-bind:class="{ 'is-selected': isUnderPath('/site-manager/orders', '/site-manager/delivery-request', '/site-manager/delivery-notes') }"
              v-bind:aria-current="isUnderPath('/site-manager/orders', '/site-manager/delivery-request', '/site-manager/delivery-notes') ? 'page' : undefined"
              v-bind:aria-label="t('tabs.orders')"
              v-on:click="goTo('SiteManagerOrders')">
        <ion-icon v-bind:icon="clipboardOutline" class="footer-menu-icon"/>
        <span class="footer-menu-label">{{ t('tabs.orders') }}</span>
      </button>
      <button v-else class="footer-menu-item footer-placeholder" disabled aria-hidden="true" tabindex="-1"></button>

      <button v-if="isValidated"
              type="button"
              class="footer-menu-item tab-home"
              v-bind:class="{ 'is-selected': isActive('SiteManagerDashboard') }"
              v-bind:aria-current="isActive('SiteManagerDashboard') ? 'page' : undefined"
              v-bind:aria-label="t('tabs.dashboard')"
              v-on:click="goTo('SiteManagerDashboard')">
        <span class="home-bubble" v-bind:class="{ 'home-bubble-active': isActive('SiteManagerDashboard') }">
          <ion-icon v-bind:icon="homeOutline" class="footer-menu-icon"/>
        </span>
        <span class="footer-menu-label">{{ t('tabs.dashboard') }}</span>
      </button>
      <button v-else
              type="button"
              class="footer-menu-item"
              v-bind:class="{ 'is-selected': isActive(SiteManagerDeliveryCallsRoute) }"
              v-bind:aria-current="isActive(SiteManagerDeliveryCallsRoute) ? 'page' : undefined"
              v-bind:aria-label="t('tabs.planning')"
              v-on:click="goTo(SiteManagerDeliveryCallsRoute)">
        <ion-icon v-bind:icon="calendarOutline" class="footer-menu-icon"/>
        <span class="footer-menu-label">{{ t('tabs.planning') }}</span>
      </button>

      <button v-if="isValidated"
              type="button"
              class="footer-menu-item"
              v-bind:class="{ 'is-selected': isActive(SiteManagerDeliveryCallsRoute) }"
              v-bind:aria-current="isActive(SiteManagerDeliveryCallsRoute) ? 'page' : undefined"
              v-bind:aria-label="t('tabs.planning')"
              v-on:click="goTo(SiteManagerDeliveryCallsRoute)">
        <ion-icon v-bind:icon="calendarOutline" class="footer-menu-icon"/>
        <span class="footer-menu-label">{{ t('tabs.planning') }}</span>
      </button>
      <button v-else class="footer-menu-item footer-placeholder" disabled aria-hidden="true" tabindex="-1"></button>

      <button type="button"
              class="footer-menu-item"
              v-bind:aria-label="t('tabs.more')"
              v-on:click="openMoreSheet">
        <ion-icon v-bind:icon="menuOutline" class="footer-menu-icon"/>
        <span class="footer-menu-label">{{ t('tabs.more') }}</span>
      </button>
    </nav>
  </ion-footer>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import {useI18n} from 'vue-i18n'
import {useRoute, useRouter} from 'vue-router'
import type {RouteParamsRaw} from 'vue-router'
import {
  IonFooter,
  IonIcon,
  actionSheetController
} from '@ionic/vue'
import {
  homeOutline,
  clipboardOutline,
  pricetagOutline,
  menuOutline,
  calendarOutline,
  syncOutline,
  cogOutline,
  logOutOutline,
} from 'ionicons/icons'
import {SiteManagerSecondeVieRoute, SiteManagerDeliveryCallsRoute} from '@/router/sitemanager'
import {mapActions, mapGetters} from 'vuex'

export default defineComponent({
  name: 'SiteManagerFooterMenu',
  components: {
    IonFooter,
    IonIcon,
  },
  setup() {
    const {t} = useI18n({inheritLocale: true, useScope: 'local'})
    const route = useRoute()
    const router = useRouter()

    const isActive = (name: string) => route.name === name
    const isActiveGroup = (names: string[]) => names.includes(route.name as string)
    const isUnderPath = (...prefixes: string[]) => prefixes.some(p => route.path.startsWith(p))
    const goTo = (name: string, params?: RouteParamsRaw) =>
      router.push({name, params})

    return {
      t, route, router,
      isActive, isActiveGroup, isUnderPath, goTo,
      homeOutline, clipboardOutline, pricetagOutline, menuOutline,
      calendarOutline, syncOutline, cogOutline, logOutOutline,
      SiteManagerDeliveryCallsRoute,
    }
  },
  methods: {
    ...mapActions(['logout']),

    async openMoreSheet() {
      const buttons: any[] = [
        {
          text: this.t('more.secondeVie'),
          icon: syncOutline,
          handler: () => this.router.push({name: SiteManagerSecondeVieRoute})
        },
        { text: this.t('group.account'), cssClass: 'action-sheet-group-label', handler: () => false },
        {
          text: this.t('more.parameters'),
          icon: cogOutline,
          handler: () => this.router.replace({name: 'Parameters'})
        },
        {
          text: this.t('more.logout'),
          icon: logOutOutline,
          role: 'destructive',
          cssClass: 'logout-button',
          handler: async () => {
            await this.logout()
            await this.router.replace({name: 'Login'})
          }
        },
        {text: this.t('cancel'), role: 'cancel'}
      ]
      const sheet = await actionSheetController.create({
        cssClass: 'sm-action-sheet',
        header: this.t('more.title'),
        buttons,
      })
      await sheet.present()
    }
  },
  computed: {
    ...mapGetters(['currentUser']),
    isValidated(): boolean {
      return this.currentUser?.validatedUser !== false
    },
  },
})
</script>

<style>
/* Non-scoped: Ionic shadow DOM components require global selectors */

/* ── Action sheets ─────────────────────────────────────────────────── */
.sm-action-sheet {
  --button-color: var(--ion-text-color);
  --color: var(--ion-text-color);
}

/* Group label (non-clickable separator) */
.sm-action-sheet .action-sheet-group-label {
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ion-color-rockease) !important;
  background: var(--ion-color-light) !important;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  min-height: 28px !important;
  pointer-events: none;
  opacity: 1 !important;
}

/* Hide the ripple effect on group labels */
.sm-action-sheet .action-sheet-group-label::after {
  display: none !important;
}

/* Header title */
.sm-action-sheet .action-sheet-title {
  font-family: var(--ion-font-family);
  font-weight: 600;
  font-size: 15px;
  color: var(--ion-color-rockease) !important;
  border-bottom: 2px solid var(--ion-color-rockease);
  padding-bottom: 12px;
}

/* Regular buttons */
.sm-action-sheet .action-sheet-button {
  font-family: var(--ion-font-family);
  font-weight: 400;
  color: var(--ion-text-color);
}

/* Icons on buttons */
.sm-action-sheet .action-sheet-button .action-sheet-icon {
  color: var(--ion-color-rockease);
}

/* Add buttons (addOutline icon) — slightly bolder */
.sm-action-sheet .action-sheet-button:has(.action-sheet-icon) {
  font-weight: 500;
}

/* Destructive button (logout) */
.sm-action-sheet .action-sheet-destructive,
.sm-action-sheet .action-sheet-destructive .action-sheet-icon,
.logout-button,
.logout-button .action-sheet-icon {
  color: #eb445a !important;
}

/* Cancel button */
.sm-action-sheet .action-sheet-cancel {
  font-weight: 600;
  color: var(--ion-color-rockease) !important;
}

ion-footer.site-manager-footer {
  --background: transparent;
}

.footer-menu {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  width: 100%;
  background: var(--ion-color-light);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 4px;
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 4px);
}

.footer-menu-item {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  background: transparent;
  color: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  font: inherit;
  line-height: 1.1;
  min-height: 56px;
  min-width: 0;
  padding: 6px 2px 4px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  -webkit-tap-highlight-color: transparent;
}

.footer-menu-item.is-selected {
  color: var(--ion-color-rockease);
}

.footer-menu-icon {
  color: currentColor;
  font-size: 20px !important;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  line-height: 1;
  transform: none !important;
}

.md .footer-menu-icon {
  font-size: 18px !important;
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
}

.footer-menu-label {
  display: block;
  max-width: 100%;
  min-width: 0;
  font-size: 11px;
  line-height: 1.05;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.md .footer-menu-label {
  font-size: 10px;
}

.home-bubble {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--ion-color-light);
  margin-top: -4px;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
  transition: background 0.2s, box-shadow 0.2s;
}

.tab-home .footer-menu-icon {
  font-size: 18px !important;
  width: 18px;
  height: 18px;
  flex-basis: 18px;
}

.home-bubble-active {
  background: var(--ion-color-rockease) !important;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
}

.home-bubble-active ion-icon {
  color: #fff !important;
}

.footer-placeholder {
  visibility: hidden;
  pointer-events: none;
}
</style>

<i18n lang="yaml">
fr:
  cancel: "Annuler"
  group:
    consult: "Consulter"
    create: "Créer"
    orders: "Commandes"
    pickups: "Enlèvements"
    notes: "Bons de livraison"
    tools: "Outils"
    account: "Compte"
  tabs:
    dashboard: "Accueil"
    orders: "Commandes"
    quotations: "Devis"
    planning: "Planif."
    more: "Plus"
  orders:
    title: "Commandes & Appels & BL"
    list: "Commandes"
    addExpressOrder: "Nouvelle commande express"
  quotations:
    title: "Devis"
    list: "Demandes de devis"
    add: "Nouvelle demande"
  deliveries:
    title: "Appels de livraison / enlèvement"
    deliveryPending: "Livraisons en cours"
    deliveryAccepted: "Livraisons acceptées"
    deliveryDelivered: "Livraisons terminées"
    disputes: "Litiges"
    pickupPending: "Enlèvements en cours"
    addDelivery: "Nouvel appel livraison"
    addPickup: "Nouvel appel enlèvement"
  notes:
    title: "Bons de livraison"
    upcoming: "En cours"
    delivered: "Terminés"
    addPickup: "Nouveau bon de départ"
  more:
    title: "Plus"
    secondeVie: "Bourse aux Matériaux"
    parameters: "Paramètres"
    logout: "Se déconnecter"

en:
  cancel: "Cancel"
  group:
    consult: "Browse"
    create: "Create"
    orders: "Orders"
    pickups: "Pickups"
    notes: "Delivery notes"
    tools: "Tools"
    account: "Account"
  tabs:
    dashboard: "Home"
    orders: "Orders"
    quotations: "Quotes"
    planning: "Planning"
    more: "More"
  orders:
    title: "Orders & Calls & DN"
    list: "Orders"
    addExpressOrder: "New express order"
  quotations:
    title: "Quotes"
    list: "Quote requests"
    add: "New quote request"
  deliveries:
    title: "Delivery / Pickup calls"
    deliveryPending: "Deliveries in progress"
    deliveryAccepted: "Accepted deliveries"
    deliveryDelivered: "Completed deliveries"
    disputes: "Disputes"
    pickupPending: "Pickups in progress"
    addDelivery: "New delivery call"
    addPickup: "New pickup call"
  notes:
    title: "Delivery notes"
    upcoming: "Upcoming"
    delivered: "Delivered"
    addPickup: "New pickup note"
  more:
    title: "More"
    secondeVie: "Bourse aux Matériaux"
    parameters: "Parameters"
    logout: "Log out"
</i18n>
